import React, { useCallback, useEffect, useState } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Snackbar, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import LinearGradient from 'react-native-linear-gradient';
import { useAuth } from '../context/AuthContext';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';
import JobService from '../services/jobService';
import AddJobBottomSheet from '../components/AddJobBottomSheet';
import PickUpJobSheet from '../components/PickUpJobSheet';
import CompleteJobSheet from '../components/CompleteJobSheet';
import ReleaseJobDialog from '../components/ReleaseJobDialog';
import JobCard from '../components/JobCard';
import FloatingActionButton from '../components/FloatingActionButton';
import { Job } from '../types/Job';
import { ErrorLogger } from '../utils/errorLogger';
import { getGreeting } from '../utils/greeting';
import { formatAmount as fmt } from '../utils/formatAmount';

const DriverJobsScreen: React.FC = () => {
  const theme = useTheme();
  const { user, userProfile, signOut } = useAuth();

  const [pickupJob, setPickupJob] = useState<Job | null>(null);
  const [pickupSheetVisible, setPickupSheetVisible] = useState(false);

  const [releaseDialogVisible, setReleaseDialogVisible] = useState(false);
  const [completeSheetVisible, setCompleteSheetVisible] = useState(false);

  const [completedJobs, setCompletedJobs] = useState<Job[]>([]);

  const [addJobVisible, setAddJobVisible] = useState(false);

  const [snackMessage, setSnackMessage] = useState('');
  const [snackVisible, setSnackVisible] = useState(false);

  const loadCompletedJobs = useCallback(async () => {
    if (!user?.uid) return;
    const jobs = await JobService.getCompletedJobsByDriver(user.uid);
    setCompletedJobs(jobs);
  }, [user?.uid]);

  useEffect(() => { loadCompletedJobs(); }, [loadCompletedJobs]);

  const poolSubscribe = useCallback(
    (onUpdate: (jobs: Job[]) => void, onError: (error: Error) => void) =>
      JobService.subscribeToJobPool(onUpdate, onError),
    [],
  );

  const driverJobSubscribe = useCallback(
    (onUpdate: (jobs: Job[]) => void, onError: (error: Error) => void) => {
      if (!user?.uid) {
        onUpdate([]);
        return () => {};
      }
      return JobService.subscribeToDriverJob(user.uid, onUpdate, onError);
    },
    [user?.uid],
  );

  const { data: pendingJobs } = useFirestoreSubscription<Job[]>(poolSubscribe, []);
  const { data: assignedJobs } = useFirestoreSubscription<Job[]>(driverJobSubscribe, []);
  const activeJob = assignedJobs[0] ?? null;

  const showSnack = (msg: string) => {
    setSnackMessage(msg);
    setSnackVisible(true);
  };

  const handlePickUpPress = (job: Job) => {
    setPickupJob(job);
    setPickupSheetVisible(true);
  };

  const handleConfirmPickUp = async () => {
    if (!pickupJob?.id || !user?.uid || !userProfile?.assignedTruckId) return;
    try {
      await JobService.pickUpJob(
        pickupJob.id,
        user.uid,
        userProfile.assignedTruckId,
        user.displayName ?? '',
      );
      setPickupSheetVisible(false);
      setPickupJob(null);
      showSnack('Job picked up!');
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriverJobsScreen.handleConfirmPickUp');
      setPickupSheetVisible(false);
      showSnack('Failed to pick up job.');
    }
  };

  const handleRelease = async () => {
    if (!activeJob?.id) return;
    try {
      await JobService.releaseJob(activeJob.id);
      setReleaseDialogVisible(false);
      showSnack('Job released.');
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriverJobsScreen.handleRelease');
      setReleaseDialogVisible(false);
      showSnack('Failed to release job.');
    }
  };

  const handleComplete = async (paymentType: 'cash' | 'debt', serviceCost: number) => {
    if (!activeJob?.id || !user?.uid) return;
    try {
      await JobService.completeJob(
        activeJob.id,
        paymentType,
        serviceCost,
        user.uid,
        user.displayName ?? '',
      );
      setCompleteSheetVisible(false);
      showSnack('Job completed!');
      await loadCompletedJobs();
    } catch (error) {
      ErrorLogger.logError(error as any, 'DriverJobsScreen.handleComplete');
      setCompleteSheetVisible(false);
      showSnack('Failed to complete job.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // handled by AuthContext
    }
  };

  const firstName = user?.displayName?.split(' ')[0] ?? 'Driver';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View>
          <Text style={[styles.headerGreeting, { color: theme.colors.onSurface }]}>
            {getGreeting()}, {firstName}
          </Text>
          <Text style={[styles.headerSubtitle, { color: theme.colors.onSurfaceVariant }]}>
            Here's your job board
          </Text>
        </View>
        <TouchableOpacity
          testID="driver-signout-button"
          style={[styles.avatarButton, { backgroundColor: theme.colors.primary }]}
          onPress={handleSignOut}
        >
          {user?.photoURL ? (
            <Image source={{ uri: user.photoURL }} style={styles.avatar} />
          ) : (
            <MaterialCommunityIcons
              name="account-circle"
              size={20}
              color={theme.colors.onPrimary}
            />
          )}
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>

        {/* ── Active Job ── */}
        <Text style={[styles.sectionLabel, { color: theme.colors.onSurfaceVariant }]}>
          MY ACTIVE JOB
        </Text>

        {activeJob ? (
          <View testID="active-job-section" style={styles.activeWrapper}>
            <LinearGradient
              colors={['#063669', '#274e82']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.activeCard}
            >
              <Text style={styles.activeStatus}>IN PROGRESS</Text>

              <Text testID="active-job-route" style={styles.activeRoute}>
                {activeJob.from} → {activeJob.to}
              </Text>

              <Text testID="active-job-amount" style={styles.activeAmount}>
                {fmt(activeJob.intakeAmount)}
              </Text>

              {activeJob.cargoTags.length > 0 && (
                <Text testID="active-job-tags" style={styles.activeTags}>
                  {activeJob.cargoTags.join(', ')}
                </Text>
              )}

              {activeJob.description ? (
                <Text testID="active-job-desc" style={styles.activeDesc}>
                  {activeJob.description}
                </Text>
              ) : null}

              <View style={styles.activeActions}>
                <TouchableOpacity
                  testID="release-button"
                  onPress={() => setReleaseDialogVisible(true)}
                  style={styles.releaseBtn}
                >
                  <MaterialCommunityIcons name="undo-variant" size={16} color="white" />
                  <Text style={[styles.releaseBtnText, { marginLeft: 6 }]}>Release</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  testID="complete-button"
                  onPress={() => setCompleteSheetVisible(true)}
                  style={styles.completeBtn}
                >
                  <MaterialCommunityIcons name="flag-checkered" size={16} color="#063669" />
                  <Text style={[styles.completeBtnText, { marginLeft: 6 }]}>Complete</Text>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>
        ) : (
          <View testID="no-active-job" style={[styles.emptyCard, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.emptyCardText, { color: theme.colors.onSurfaceVariant }]}>
              No active job. Pick one up below.
            </Text>
          </View>
        )}

        {/* ── Available Jobs ── */}
        <Text style={[styles.sectionLabel, { color: theme.colors.onSurfaceVariant, marginTop: 24 }]}>
          AVAILABLE JOBS{pendingJobs.length > 0 ? ` (${pendingJobs.length})` : ''}
        </Text>

        {pendingJobs.length === 0 ? (
          <View testID="no-available-jobs" style={[styles.emptyCard, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.emptyCardText, { color: theme.colors.onSurfaceVariant }]}>
              No available jobs right now.
            </Text>
          </View>
        ) : (
          <View style={styles.jobCardList}>
            {pendingJobs.map(job => (
              <View key={job.id}>
                <JobCard
                  job={job}
                  onPickUp={handlePickUpPress}
                  pickUpDisabled={activeJob !== null}
                />
              </View>
            ))}
          </View>
        )}

        {/* ── Completed Jobs ── */}
        <Text
          testID="completed-section-label"
          style={[styles.sectionLabel, { color: theme.colors.onSurfaceVariant, marginTop: 24 }]}
        >
          COMPLETED JOBS{completedJobs.length > 0 ? ` (${completedJobs.length})` : ''}
        </Text>

        {completedJobs.length === 0 ? (
          <View testID="no-completed-jobs" style={[styles.emptyCard, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.emptyCardText, { color: theme.colors.onSurfaceVariant }]}>
              No completed jobs yet.
            </Text>
          </View>
        ) : (
          <View style={styles.jobCardList}>
            {completedJobs.map(job => (
              <View key={job.id}>
                <JobCard
                  job={job}
                />
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <PickUpJobSheet
        job={pickupJob}
        visible={pickupSheetVisible}
        onClose={() => {
          setPickupSheetVisible(false);
          setPickupJob(null);
        }}
        onConfirm={handleConfirmPickUp}
      />

      <CompleteJobSheet
        visible={completeSheetVisible}
        onClose={() => setCompleteSheetVisible(false)}
        onConfirm={handleComplete}
      />

      <ReleaseJobDialog
        visible={releaseDialogVisible}
        onDismiss={() => setReleaseDialogVisible(false)}
        onConfirm={handleRelease}
      />

      <Snackbar
        visible={snackVisible}
        onDismiss={() => setSnackVisible(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setSnackVisible(false) }}
      >
        {snackMessage}
      </Snackbar>

      <FloatingActionButton onPress={() => setAddJobVisible(true)} />

      <AddJobBottomSheet
        visible={addJobVisible}
        onClose={() => setAddJobVisible(false)}
        onSuccess={() => setAddJobVisible(false)}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerGreeting: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 22,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    marginTop: 2,
  },
  avatarButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  scroll: {
    padding: 16,
    paddingBottom: 32,
  },
  sectionLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  activeWrapper: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  activeCard: {
    borderRadius: 8,
    padding: 20,
  },
  activeStatus: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: '#7eefd0',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  activeRoute: {
    fontFamily: 'Manrope-Bold',
    fontSize: 26,
    color: 'white',
    marginBottom: 6,
  },
  activeAmount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 4,
  },
  activeTags: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 4,
  },
  activeDesc: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 16,
    lineHeight: 18,
  },
  activeActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 8,
  },
  releaseBtn: {
    flex: 1,
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
    borderRadius: 6,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  releaseBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: 'white',
  },
  completeBtn: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#7eefd0',
    borderRadius: 6,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completeBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#063669',
  },
  emptyCard: {
    borderRadius: 8,
    padding: 20,
    alignItems: 'center',
  },
  emptyCardText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    textAlign: 'center',
  },
  jobCardList: {
    gap: 10,
  },
});

export default DriverJobsScreen;
